import React, { useMemo, useRef, useState, forwardRef } from "react";
import Button, { ButtonProps } from "@ui-kit/form/Button/Button.web";
import { HorizontalArrowButtons } from "@/components/atoms/buttons";
import Box, { BoxProps } from "@ui-kit/layout/Box/Box.web";
import { enums } from "@shared/constants";
import { useTranslation } from "@shared/utils/internationalization";

import SwiperCore, { Navigation, Mousewheel, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css"; // Import Swiper styles
import Text from "@ui-kit/typography/Text/Text.web";
import Link from "next/link";
import { isTouchDevice } from "@/helpers/browser-utils";
import { CityLandingPageCategorySerializer } from "@shared/modules/caching/types";

SwiperCore.use([Navigation, Mousewheel, A11y]);

const RootContainer = forwardRef<HTMLDivElement, BoxProps>((props, ref) => (
  <Box
    ref={ref}
    position="relative"
    overflow="hidden"
    width="100%"
    // TODO: Move swiper and fade effect styles to CSS module
    // eslint-disable-next-line showpass-custom/prefer-semantic-props
    css={{
      ".swiper-wrapper": {
        display: "flex",
      },
      "&::after": {
        content: '""',
        pointerEvents: "none",
        background:
          "linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.5) 25%, rgba(255, 255, 255, 0.75) 50%, rgba(255, 255, 255, 1) 90%)",
        position: "absolute",
        top: 0,
        right: 0,
        height: "100%",
        width: "24px",
        zIndex: 1,
      },
      "@media (min-width: 480px)": {
        "&::after": {
          display: "none",
        },
      },
    }}
    {...props}
  />
));
RootContainer.displayName = "RootContainer";

const ArrowsContainer = forwardRef<HTMLDivElement, BoxProps>((props, ref) => (
  <Box
    ref={ref}
    position="absolute"
    top="none"
    right="none"
    zIndex="5"
    bg="bg"
    background="linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.5) 8%, rgba(255, 255, 255, 0.75) 16%, rgba(255, 255, 255, 1) 32%)"
    paddingLeft="4xl"
    height="100%"
    display={{ base: "none", xs: "flex" }}
    alignItems="center"
    {...props}
  />
));
ArrowsContainer.displayName = "ArrowsContainer";

export const CategoryPillButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => (
    <Button
      ref={ref}
      fontSize="0.875rem"
      // TODO: Add radii token for 20px (between xl=16px and 2xl=24px)
      // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
      borderRadius="20px"
      padding="6px 8px"
      margin="2xs"
      color="font.dark"
      fontWeight="600"
      backgroundColor="bg"
      _active={{
        backgroundColor: "primary.500",
        color: "white",
      }}
      _focus={{
        backgroundColor: "primary.500",
        color: "white",
      }}
      _hover={{
        backgroundColor: "primary.500",
        color: "white",
      }}
      // TODO: Use data-active attribute styling via component variant
      // eslint-disable-next-line showpass-custom/prefer-semantic-props
      css={{
        "&[data-active]": {
          backgroundColor: "var(--chakra-colors-primary-500)",
          color: "white",
        },
      }}
      {...props}
    />
  )
);
CategoryPillButton.displayName = "CategoryPillButton";

export type EventCategorySliderProps = {
  ButtonComponent?: React.FC<
    ButtonProps & {
      category: (typeof enums.EVENT_CATEGORIES_MAP)[keyof typeof enums.EVENT_CATEGORIES_MAP];
    }
  >;
  btnSize?: ButtonProps["size"];
  /**
   * If provided, the categories will be taken from this array instead of the enums
   */
  customCategories?: CityLandingPageCategorySerializer[];
  swiperProps?: (typeof Swiper)["defaultProps"];
};

/**
 * A Swiper component that displays all the event categories
 * Event categories are taken from the enums.
 */
const EventCategorySlider: React.FC<EventCategorySliderProps> = ({
  ButtonComponent = null,
  btnSize = "xs",
  customCategories = null,
  swiperProps = null,
}) => {
  const { t } = useTranslation("discovery");

  const swiperRef = useRef(null);

  const [disableLeft, setDisableLeft] = useState(
    swiperRef.current?.isBeginning
  );
  const [disableRight, setDisableRight] = useState(swiperRef.current?.isEnd);

  const categories = useMemo(
    () =>
      customCategories?.length
        ? customCategories.sort((a, b) => (a.name < b.name ? -1 : 1))
        : enums.EVENT_CATEGORIES_MAP_BY_LIST,
    [customCategories]
  );

  return (
    <RootContainer aria-label={t("navigation", { ns: "common" })} w="100%">
      <Swiper
        // Enables freeMode for smooth momentum-based scrolling
        freeMode
        // Allows touch gestures on touch devices for a better mobile experience
        allowTouchMove={isTouchDevice()}
        // Sets a small threshold to differentiate between a swipe and a simple tap
        threshold={10}
        // Ensures slight movements don't interfere with normal clicks
        preventClicks={false}
        // Allows click events to propagate normally
        preventClicksPropagation={false}
        // Excludes noSwipingSelector so swiping can start even on links or buttons
        slidesOffsetAfter={90} // Leaves space at the end for arrow controls
        spaceBetween={3} // Sets spacing between slides
        slidesPerView="auto" // Dynamically adjusts slide width based on content
        // Configures mouse wheel navigation for desktop
        mousewheel={{
          forceToAxis: true, // Restricts scrolling to one axis
          sensitivity: 1, // Sets scroll sensitivity
        }}
        // Disables the left arrow when the swiper reaches the beginning
        onReachBeginning={() => {
          setDisableLeft(true);
        }}
        // Disables the right arrow when the swiper reaches the end
        onReachEnd={() => {
          setDisableRight(true);
        }}
        // Updates arrow states on slide change
        onSlideChange={() => {
          setDisableLeft(swiperRef.current?.isBeginning);
          setDisableRight(swiperRef.current?.isEnd);
        }}
        // Updates arrow states on manual scrolling
        onScroll={() => {
          setDisableLeft(swiperRef.current?.isBeginning);
          setDisableRight(swiperRef.current?.isEnd);
        }}
        // Captures the swiper instance for further interaction
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        // Spreads additional props for customizability
        {...swiperProps}
      >
        {categories.map((category) => (
          <SwiperSlide
            key={category.id}
            style={{
              width: "max-content", // Ensures the slide adjusts to content width
            }}
          >
            {ButtonComponent ? (
              ButtonComponent({
                category,
                "data-testid": `event-category-slider-${category.id}`, // Adds test ID for QA
              })
            ) : (
              <Link href={category.id} passHref>
                <CategoryPillButton
                  data-testid={`event-category-slider-${category.id}`} // Adds test ID for QA
                  size={btnSize}
                  tabIndex={-1} // Excludes button from tab order
                >
                  <Text color="inherit" mt="none">
                    {t(category.name, { ns: "constants" })}
                  </Text>
                </CategoryPillButton>
              </Link>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      <ArrowsContainer>
        <HorizontalArrowButtons
          onClickLeft={() => {
            swiperRef.current?.slidePrev();
          }}
          onClickRight={() => {
            swiperRef.current?.slideNext();
          }}
          aria-label={t("change category selection page")}
          size="xs"
          leftButtonProps={{ disabled: disableLeft }}
          rightButtonProps={{ disabled: disableRight }}
        />
      </ArrowsContainer>
    </RootContainer>
  );
};

export default EventCategorySlider;
